import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslate } from "react-polyglot";
import { Typography, Box, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { useBaseData } from "../../../Providers/Data/BaseDataProvider.jsx";
import PrimaryButton from "../../../SharedComponents/PrimaryButton.jsx";
import { useAuth } from "../../../Providers/Auth/AuthProvider.jsx";
import DiscardButton from "../../Offer/components/DiscardButton.jsx";
import BuyOrSubscribeButton from "../../Offer/components/BuyOrSubscribeButton.jsx";
import { calculateEndDateOfAccess, checkOfferRepeat } from "../../../Utils/misc.js";
import useCompanyPublicId from "../../../hooks/useCompanyPublicId.js";
import { usePayment } from "../../../Providers/Payment/PaymentProvider.jsx";

const useStyles = makeStyles((theme) => ({
  card: {
    position: "relative",
  },
  media: {
    height: 250,
  },
  content: {
    textAlign: "center",
    padding: 30,
  },
  priceLabel: {
    position: "absolute",
    top: 7,
    left: 7,
    backgroundColor: "rgba(250,250,250,0.8)",
    borderRadius: 4,
    padding: 15,
    minWidth: 94,
    textAlign: "center",
  },
  price: {
    fontSize: 22,
    fontWeight: 700,
  },
  oldPrice: {
    fontSize: 20,
    color: "#e31b1b",
    textDecoration: "line-through",
  },
  newPrice: {
    fontSize: 22,
    fontWeight: 700,
    color: "#199f19",
    marginLeft: 8,
  },
  cardBtn: {
    height: "fit-content",
    padding: "8px 18px",
    borderRadius: 19,
  },
  levelsExistMessage: {
    color: "#1a854b",
  },
  firstPrice: {
    fontSize: 14,
    fontWeight: 500,
  },
  cardBox: {
    display: "flex",
    alignItems: "center",
    position: "relative",
    backgroundColor: "#fff",
    border: "2px solid rgba(0, 0, 0, .08)",
    borderRadius: theme.spacing(2),
    padding: "16px 20px",
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  imageBox: {
    width: 170,
    height: 170,
    borderRadius: 14,
    overflow: "hidden",
    flexShrink: 0,
    cursor: "pointer",
  },
  offerImg: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "cover",
    objectPosition: "center",
  },
  descriptionBox: {
    marginLeft: 20,
    flexGrow: 1,
  },
  titleCard: {
    fontSize: 20,
    fontWeight: 500,
    color: "#1B1C1C",
    height: 30,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    cursor: "pointer",
  },
  descriptionCard: {
    fontSize: 16,
    color: "#3D4853",
    height: 50,
    display: "-webkit-box",
    "-webkit-line-clamp": 3,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
  },
  buttonBox: {
    marginLeft: theme.spacing(1),
    "& .MuiTypography-root": {
      textAlign: "right",
    },
  },
  promoCode: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
    marginRight: theme.spacing(1),
  },
}));

/**
 * @param {Offer} offer
 * @param {UserInfo} user
 * @returns {*}
 * */
const OfferCard = ({ offer, user }) => {
  const classes = useStyles();
  const t = useTranslate();
  const history = useHistory();
  const { company2 } = useAuth();
  const { company, userInfo } = useBaseData();
  const { companyName } = useCompanyPublicId();
  const { userValidPromoCode, setUserValidPromoCode } = usePayment();

  const company_ = company || company2;
  const schoolName = company_ && company_.urn;
  const [endDate, setEndDate] = useState("");
  const [isOfferRepeat, setIsOfferRepeat] = useState(false);
  const [ifLevelExist, setIfLevelExist] = useState(false);
  const [userOffer, setUserOffer] = useState(/** @type UserOffer */ null);
  const [promoCode, setPromoCode] = useState(""); // значение введенное в поле
  const [foundPromo, setFoundPromo] = useState(null); // найденный промокод
  const [userSubscribe, setUserSubscribe] = useState(/** @type UserSubscribe */ null);
  const [isValid, setIsValid] = useState(false); // введенный промокод валидный
  const [isValidCodes, setIsValidCodes] = useState(false); // если есть хотя бы один валидны промокод

  // проверяет, есть ли валидные промокоды у предложения
  useEffect(() => {
    if (!offer?.promoCodes?.length) return;
    if (offer?.promoCodes.some((item) => !item.endDate || item.endDate > Date.now())) {
      setIsValidCodes(true);
    } else {
      setIsValidCodes(false);
    }
  }, [offer]);

  // если есть промокод, введенный на другой странице, то устанавливаем его
  useEffect(() => {
    if (userValidPromoCode && offer) {
      const foundPromo = offer.promoCodes?.find((item) => {
        return item.text?.trim().toLowerCase() === userValidPromoCode;
      });

      if (foundPromo) {
        setFoundPromo(foundPromo);
      } else {
        setFoundPromo(null);
      }
      const isNotExpired = foundPromo ? !foundPromo.endDate || foundPromo.endDate > Date.now() : false;
      if (!foundPromo || !isNotExpired) {
        setIsValid(false);
      } else {
        setPromoCode(userValidPromoCode);
        setIsValid(true);
      }
    }
  }, [offer]);

  //checks the possibility of buying an offer again
  useEffect(() => {
    checkOfferRepeat(offer, setIsOfferRepeat);
  }, [offer]);

  //calculate the end date of access
  useEffect(() => {
    calculateEndDateOfAccess(user, setEndDate);
  }, [user]);

  useEffect(() => {
    // find purchased offers
    const offerOfUser = user?.offers?.find((uOffer) => uOffer.offerId === offer.id);
    setUserOffer(offerOfUser);

    const subscribeOfUser = offer && userInfo?.subscribes.find((uSubscribe) => uSubscribe.offerId === offer.id);
    setUserSubscribe(subscribeOfUser);

    // check if the level exists
    if (!user) {
      setIfLevelExist(false);
    } else {
      user.levelPacks === undefined
        ? setIfLevelExist(false)
        : setIfLevelExist(offer.levelPacks.filter((level) => user.levelPacks.includes(level)).length > 0);
    }
  }, [user, userInfo, offer]);

  // проверяет и сравнивает введенный текст и существующие промокоды
  useEffect(() => {
    const promo = promoCode?.trim().toLowerCase();
    if (!isValidCodes || !promo) return;

    const foundPromo = offer.promoCodes.find((item) => {
      return item.text?.trim().toLowerCase() === promo;
    });
    if (foundPromo) {
      setFoundPromo(foundPromo);
    } else {
      setFoundPromo(null);
    }
    const isNotExpired = foundPromo ? !foundPromo.endDate || foundPromo.endDate > Date.now() : false;
    if (!foundPromo || !isNotExpired) {
      setIsValid(false);
    } else {
      setIsValid(true);
      // сохраняем валидный промокод в глобальном состоянии оплаты
      setUserValidPromoCode(promo);
    }
  }, [promoCode, offer]);

  /** */
  const onClickDetails = () => {
    history.push({
      pathname: `${companyName ? "/" + companyName : ""}/offer/${schoolName}/${offer.id}`,
    });
  };

  return (
    <>
      <Box key={offer.id} className={classes.cardBox} onClick={() => {}}>
        <Box className={classes.imageBox} onClick={onClickDetails}>
          <img className={classes.offerImg} src={offer.image.url} alt={offer.image.name} />
        </Box>

        <Box className={classes.descriptionBox}>
          <Typography className={classes.titleCard} onClick={onClickDetails}>
            {offer.name}
          </Typography>
          <Typography className={classes.descriptionCard}>
            <span dangerouslySetInnerHTML={offer.description.sanitizedData()} />
          </Typography>
          <Box display="flex" mt={5} justifyContent="space-between" alignItems="end">
            <Box display="flex" alignItems="center">
              {offer.oldPrice && offer.oldPrice > offer.price ? (
                <>
                  <Typography className={classes.oldPrice} variant="subtitle1">
                    {offer.oldPrice} {offer.currency.toUpperCase()}
                  </Typography>
                  <Typography className={classes.newPrice} variant="subtitle1">
                    {foundPromo ? offer.price - foundPromo.discountAmount : offer.price} {offer.currency.toUpperCase()}
                  </Typography>
                </>
              ) : (
                <Typography variant="subtitle1" className={classes.price}>
                  {foundPromo ? offer.price - foundPromo.discountAmount : offer.price} {offer.currency.toUpperCase()}
                </Typography>
              )}
            </Box>
            <Box display="flex" justifyContent="end" alignItems="end">
              {isValidCodes && (
                <TextField
                  label={t("promoCode")}
                  name="promoCode"
                  variant="outlined"
                  size="small"
                  className={classes.promoCode}
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  helperText={promoCode && !isValid && t("promoInvalid")}
                />
              )}
              <PrimaryButton className={classes.cardBtn} onClick={onClickDetails}>
                <Typography>{t("details")}</Typography>
              </PrimaryButton>
              <Box className={classes.buttonBox}>
                {offer.isSubscribe && offer.subscribe && userSubscribe?.endTime && (
                  <Typography variant="body2">
                    {t("expired")}: {userSubscribe.endTime}
                  </Typography>
                )}
                <BuyOrSubscribeButton {...{ offer, isOfferRepeat, userOffer, userSubscribe, promoCode }} />
              </Box>
              <DiscardButton
                className={classes.cardBtn}
                offer={offer}
                user={user}
                offerProceed={userOffer?.status === "pending"}
              />
            </Box>
          </Box>
          <Box mt={0.5}>
            {offer.isSubscribe && offer.subscribe && (
              <Typography variant="body2" className={classes.firstPrice} align="right">
                {t("firstPayment")}{" "}
                <strong>
                  {offer.subscribe.firstPrice} {offer.currency.toUpperCase()}
                </strong>
              </Typography>
            )}
            {ifLevelExist && !userOffer && (
              <Typography variant="body2" className={classes.levelsExistMessage} component="small">
                {t("levelPackExist")}
              </Typography>
            )}
            {userOffer?.status === "pending" && (
              <Typography variant="body2" className={classes.levelsExistMessage} component="small">
                {t("awaitingPayment")}
              </Typography>
            )}
            {userOffer?.paid && !isOfferRepeat && (
              <Typography variant="body2" className={classes.levelsExistMessage} component="small">
                {t("bought")}
              </Typography>
            )}
            {userOffer?.paid && isOfferRepeat && (
              <Typography variant="body2" className={classes.levelsExistMessage} component="small">
                {t("boughtUntilDate", { endDate })}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

OfferCard.propTypes = {
  offer: PropTypes.object,
  user: PropTypes.object,
};

export default OfferCard;
